import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Tables } from "../../components/Elements/Tables/Tables";
import CustomSwitch from "../../components/Elements/CustomSwitch/CustomSwitch";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/Elements/Search/SearchBar";
import { API_URLS } from "../../api/apiUrls";
import TextStyles from "../../components/Elements/TextStyles/TextStyles";
import { getApi, postApi } from "../../api/api_instance";
import { putApi } from "../../api/api_instance";
import { ReactComponent as CopyIcon } from "../../assets/svgs/greenFile.svg";
import { ReactComponent as EditIcon } from "../../assets/svgs/greenEdit.svg";
import CustomButton from "../../components/Elements/Buttons/CustomButtons";
import { ReactComponent as EyeIcon } from "../../assets/svgs/greenEye.svg";
import CreateStandardForms from "./DialogBoxes/CreateStandardForms";
import { Badge } from "../../components/Elements/TextStyles/Badge";
import dayjs from "dayjs";
import EditStandardForm from "./DialogBoxes/EditStandardForm";
import Confirmation from "./DialogBoxes/Confirmation";
import CopyForm from "./DialogBoxes/CopyForm";
import PopupCard from "../../components/Cards/PopupCard/PopupCard";
import { useRef } from "react";
const StandardForms = () => {
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [sortOrder, setSortOrder] = useState("-createdAt");
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const debounceTimer = useRef(null);
  const [totalForms, setTotalForms] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [openEditStandardForm, setOpenEditStandardForm] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [openConsentPage, setOpenConsentPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formDataToConfirm, setFormDataToConfirm] = useState(null);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [openCopyForm, setOpenCopyForm] = useState(false);
  const [isCopySuccess, setCopySuccess] = useState(false);
  const [isCreateFormSuccess, setCreateFormSuccess] = useState(false);
  const [isEditFormSuccess, setEditFormSuccess] = useState(false);
  const [toggleLoadingIds, setToggleLoadingIds] = useState([]);
  const [inActivePopup, setInActivePopup] = useState(false);
  const [popup, setPopup] = useState({
    visible: false,
    type: "", // "success" | "error" | "warning"
    title: "",
    message: "",
  });
  const debounceTime = 500;
  const headers = useMemo(
    () => [
      { label: "Sr. No.", key: "serial" },
      { label: "Form Title", key: "formname" },
      { label: "Description", key: "description" },
      { label: "Role Type", key: "role_type" },
      { label: "Created On", key: "createdon" },
      { label: "Status", key: "status" },
      { label: "Action", key: "action" },
    ],
    [],
  );
  //-------------debounce for search----------
  const handleSearch = useCallback((value) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setSearchTerm(value);
      setPage(1);
    }, debounceTime);
  }, []);

  const ROLE_TYPE_LABELS = {
    employee: "EMPLOYEE",
    vendor: "VENDOR",
    // location_user: "LOCATION USER",
    // location_admin: "LOCATION ADMIN",
    user: "USER",
    sub_admin: "SUB ADMIN",
    group_admin: "GROUP ADMIN",
  };

  const showPopup = (type, title, message, duration = 3000) => {
    setPopup({ visible: true, type, title, message });
    setTimeout(() => {
      setPopup({ visible: false, type: "", title: "", message: "" });
    }, duration);
  };

  const fetchForms = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getApi({
        apiUrl: API_URLS.GET_STANDARDFORMS(
          pageSize,
          page,
          sortOrder,
          searchTerm,
        ),
      });

      if (response.success) {
        setPageSize(response.limit);
        setTotalForms(response?.total || 0);
        const formData = (response?.data || []).map((form, index) => ({
          key: form._id,
          serial: (page - 1) * pageSize + (index + 1),
          formname: form.title,
          description: form.description || "N/A",
          role_type: (form.role_type || [])
            .map((role) => ROLE_TYPE_LABELS[role] || role)
            .join(", "),

          createdon: dayjs(form.createdAt).format("DD/MM/YYYY"),
          status: (
            <div className="flex justify-center">
              {" "}
              <Badge text={form.status} />
            </div>
          ),

          action: (
            <div className="flex gap-4 justify-center items-center">
              <EyeIcon
                style={{ fontSize: "18px", cursor: "pointer" }}
                onClick={() => navigate(`/standard-forms/${form._id}`)}
              />
              {form.status === "Draft" && (
                <EditIcon
                  onClick={() => {
                    setSelectedForm(form);
                    setOpenEditStandardForm(true);
                  }}
                />
              )}
              <CopyIcon
                style={{ fontSize: "18px", cursor: "pointer" }}
                onClick={() => {
                  setOpenCopyForm(true);
                  setSelectedForm(form);
                }}
              />
              <CustomSwitch
                checked={form.status !== "Inactive"}
                disabled={false}
                loading={toggleLoadingIds.includes(form._id)}
                onChange={() => {
                  handleToggleStatus(form._id, form.status);
                }}
              />
            </div>
          ),
        }));

        setFilteredData(formData);
      } else {
        setFilteredData([]);
      }
    } catch (error) {
      console.error("Error fetching form data:", error);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [pageSize, page, sortOrder, searchTerm, openCreateDialog, navigate]);

  const handleToggleStatus = async (formId, currentStatus) => {
    if (toggleLoadingIds.includes(formId)) return; // prevent duplicate toggles

    setToggleLoadingIds((prev) => [...prev, formId]);

    try {
      let newStatus = "";
      if (currentStatus === "Inactive") {
        newStatus = "Approved";
      } else {
        newStatus = "Inactive";
      }

      const response = await putApi(
        {
          apiUrl: API_URLS.UPDATE_STANDARDFORM_STATUS(formId),
          data: { status: newStatus },
        },
        true,
      );

      if (response.success) {
        showPopup("success", "Status Changed", "Status changed Successfully");
        fetchForms();
      }
    } catch (error) {
      console.error("Error updating form status:", error);
    } finally {
      setToggleLoadingIds((prev) => prev.filter((id) => id !== formId));
    }
  };

  const handleSort = useCallback(() => {
    setSortOrder((prevOrder) =>
      prevOrder === "createdAt" ? "-createdAt" : "createdAt",
    );
  }, []);
  const handlePageChange = useCallback((page) => {
    setPage(page);
  }, []);

  const handleSaveEditData = async () => {
    try {
      const response = await putApi({
        apiUrl: API_URLS.UPDATE_STANDARDFORM_STATUS(selectedFormId),
        data: formDataToConfirm,
      });
      if (response.success) {
        // toast.success("Form Data Updated Successfully");
        setOpenConsentPage(false);
        setEditFormSuccess(true);
        fetchForms();
      }
    } catch (error) {
      console.error("Error in updating form :", error);
    }
  };

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  useEffect(() => {
    if (inActivePopup) {
      setInActivePopup(false);
    }
  }, [inActivePopup]);

  useEffect(() => {
    if (isCopySuccess) {
      showPopup(
        "success",
        "Form Copied Successfully",
        "Check Dynamic Forms to edit the copied form.",
      );
      setCopySuccess(false);
    }
  }, [isCopySuccess]);

  useEffect(() => {
    if (isCreateFormSuccess) {
      showPopup(
        "success",
        "Standard Form Created",
        "New Standard Form Created Successfully.",
      );
      setCreateFormSuccess(false);
    }
  }, [isCreateFormSuccess]);

  useEffect(() => {
    if (isEditFormSuccess) {
      showPopup("success", "Form Updated", "Form Data Updated Successfully.");
      setEditFormSuccess(false);
    }
  }, [isEditFormSuccess]);

  return (
    <div style={{ backgroundColor: "#fff" }} className="h-screen px-[20px]">
      <div className="flex justify-end text-xs font-light p-3">
        <p>Home</p>
        <span>/</span>
        <TextStyles color="gradient">Standard Form</TextStyles>
      </div>
      <div>
        <div className="flex items-center mb-6 gap-2 text-xl text-[#06513D] font-bold px-2">
          {/* <Back /> */}
          <div>Standard Form</div>
        </div>

        <div
          style={{
            border: "1px solid #3BA2B959",
            borderRadius: "10px",
            paddingTop: "20px",
            boxShadow: "0px 0px 16px 0px #3BA2B91F",
          }}
        >
          <div className="flex justify-between px-3 mb-3">
            <div className="flex gap-4 pl-3">
              <div className="px-8  h-[42px] flex items-center justify-center border border-[#06513D] bg-[#B6D4D933] rounded-md text-[#06513D] text-base font-light">
                TOTAL STANDARD FORM {totalForms}
              </div>
              <SearchBar
                width="300px"
                onSearch={(value) => {
                  handleSearch(value);
                }}
              />
            </div>
            <div className="flex gap-4">
              <CustomButton
                buttonType="button"
                variant="ok"
                buttonText=" + Create New"
                onClick={() => setOpenCreateDialog(true)}
                className="text-base font-normal h-[50px] w-[160px]"
              />
            </div>
          </div>
          <Tables
            data={filteredData}
            headers={headers}
            pageSize={pageSize}
            current={page}
            total={totalForms}
            loading={loading}
            onChange={handlePageChange}
            onSort={handleSort}
          />
        </div>
        {openCreateDialog && (
          <CreateStandardForms
            onOpen={openCreateDialog}
            onClose={() => {
              setOpenCreateDialog(false);
            }}
            onSave={async () => {
              await fetchForms();
              setOpenCreateDialog(false);
              setCreateFormSuccess(true);
            }}
          />
        )}

        {openEditStandardForm && selectedForm && (
          <EditStandardForm
            onOpen={openEditStandardForm}
            onClose={() => {
              setOpenEditStandardForm(false);
              setSelectedForm(null);
            }}
            data={selectedForm}
            onSave={(data, id) => {
              setFormDataToConfirm(data);
              setOpenEditStandardForm(false);
              setOpenConsentPage(true);
              setSelectedFormId(id);
            }}
          />
        )}

        {openConsentPage && (
          <Confirmation
            onOpen={openConsentPage}
            message="Are you sure you want to save these changes?"
            noText="Cancel"
            yesText="Save"
            onClose={() => {
              setOpenConsentPage(false);
            }}
            onConfirm={() => {
              handleSaveEditData(formDataToConfirm);
            }}
          />
        )}

        {openCopyForm && (
          <CopyForm
            onOpen={openCopyForm}
            onClose={() => {
              setOpenCopyForm(false);
            }}
            data={selectedForm}
            onSave={async () => {
              await fetchForms();
              setOpenCopyForm(false);
              setCopySuccess(true);
            }}
          />
        )}

        {popup.visible && (
          <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
            <PopupCard
              type={popup.type}
              title={popup.title}
              message={popup.message}
            />
          </div>
        )}
        {/* {inActivePopup && (
          <SuccessDailogBox
            onOpen={inActivePopup}
            onClose={() => setInActivePopup(false)}
            popuptype="in-active"
            successMessage={"Standard Form Inactive Successfully"}
          />
        )}
        {isCopySuccess && (
          <SuccessDailogBox
            onOpen={isCopySuccess}
            onClose={() => setCopySuccess(false)}
            popuptype="document"
            messageTitle="Form Copied Successfully"
            successMessage={
              "Please Check Dynamic Forms section to view and edit your copied form "
            }
          />
        )}
        {isCreateFormSuccess && (
          <SuccessDailogBox
            onOpen={isCreateFormSuccess}
            onClose={() => setCreateFormSuccess(false)}
            popuptype="success"
            successMessage={"New Standard Form Created Successfully"}
          />
        )}
        {isEditFormSuccess && (
          <SuccessDailogBox
            onOpen={isEditFormSuccess}
            onClose={() => setEditFormSuccess(false)}
            popuptype="success"
            successMessage={"Form Data Updated Successfully"}
          />
        )} */}
      </div>
    </div>
  );
};

export default StandardForms;
